import { devStackMountPrefixFromPathname } from "./devStackMount";

/** Canonical local hub gateway port — OAuth and magic links land here. */
export const DEV_PRIMARY_UI_PORT = "5173";

export function devPrimaryUiOrigin(): string {
  if (typeof window === "undefined") {
    return `http://localhost:${DEV_PRIMARY_UI_PORT}`;
  }
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:${DEV_PRIMARY_UI_PORT}`;
}

/**
 * OAuth and magic links always redirect to the gateway origin (5173) because
 * that is the origin allow-listed with Supabase. Branch stacks on other local
 * ports receive the session via the dev switcher handoff (devSessionHandoff.ts)
 * instead of running their own OAuth round-trip.
 */
export function devAuthRedirectOrigin(): string {
  return import.meta.env.DEV ? devPrimaryUiOrigin() : window.location.origin;
}

export type DevAuthOAuthRedirectInput = {
  isDev: boolean;
  windowOrigin: string;
  pathname: string;
  returnPath?: string;
};

/**
 * Hub SignIn redirectTo for local gateway stacks:
 * `https://localhost:5173/?sgs_dev_enter=<feature URL>`.
 * The boot page exchanges `code`, then enters the mount (session stays on 5173).
 */
export function buildDevAuthOAuthRedirectTo(input: DevAuthOAuthRedirectInput): string {
  const current = new URL(input.windowOrigin);
  const authOrigin = `${current.protocol}//${current.hostname}:${DEV_PRIMARY_UI_PORT}`;
  const returnPath = input.returnPath ?? "/";
  const normalizedReturn = returnPath.startsWith("/") ? returnPath : `/${returnPath}`;
  if (!input.isDev) {
    return `${input.windowOrigin.replace(/\/$/, "")}${normalizedReturn}`;
  }
  const mount = devStackMountPrefixFromPathname(input.pathname);
  if (!mount) {
    return `${authOrigin}${normalizedReturn}`;
  }
  const enterPath = normalizedReturn === "/" ? `${mount}/` : `${mount}${normalizedReturn}`;
  const enter = new URL(authOrigin);
  enter.pathname = "/";
  enter.searchParams.set("sgs_dev_enter", `${authOrigin}${enterPath}`);
  return enter.toString();
}

/**
 * When signing in from a gateway path mount, return to the operator with
 * `sgs_dev_enter` so after OAuth the session is handed back to that mount.
 */
export function devAuthOAuthRedirectTo(returnPath = "/"): string {
  const authOrigin = devAuthRedirectOrigin();
  if (!import.meta.env.DEV || typeof window === "undefined") {
    const normalizedReturn = returnPath.startsWith("/") ? returnPath : `/${returnPath}`;
    return `${authOrigin}${normalizedReturn}`;
  }
  return buildDevAuthOAuthRedirectTo({
    isDev: true,
    windowOrigin: window.location.origin,
    pathname: window.location.pathname,
    returnPath,
  });
}
